import { JSX } from 'react';

import { useShowGreenroomButton } from './use-show-greenroom-button';
import { getUser } from '@/store/slices/user/selectors';
import {
  getPublishedStatus,
  selectLoadingGreenroom,
  selectProjectIsAutoGeneratedName,
  selectSessionByUuid,
} from '@/store/slices/project/selectors';
import { useAppSelector } from '@/store/hooks';
import { EPermissions } from '@/types/permissions.types';
import { Session } from '@/types/session.types';
import userHasRoleOnActiveChannel from '@/utils/user-has-role-on-active-channel';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Button from '@ui/buttons/button/button';
import { useHandleGoToGreenroom } from './use-handle-go-to-greenroom';
import Text from '@ui/text/text/text';
import { ITooltipPortalProps } from '@ui/tooltip-portal/tooltip-portal-types';

interface Props {
  session?: Session;
  sessionUuid?: string;
  className?: string;
  TextComponent?: JSX.Element;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  hoverOpacity?: number;
  disableTooltip?: boolean;
  tooltipProps?: Partial<ITooltipPortalProps>;
}

const GreenroomButton = ({
  backgroundColor,
  hoverBackgroundColor,
  hoverOpacity,
  className,
  TextComponent,
  session: propsSession,
  sessionUuid,
  disableTooltip = true,
  tooltipProps,
}: Props) => {
  const isProjectAutoGeneratedName = useAppSelector(selectProjectIsAutoGeneratedName);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const user = useAppSelector(getUser);
  const loadingGreenroom = useAppSelector(selectLoadingGreenroom);
  const _session = useAppSelector(selectSessionByUuid(sessionUuid));
  const session = propsSession || _session;

  const publishedUrl = publishedStatus?.url;

  const handleGoToGreenroom = useHandleGoToGreenroom();

  const allowedPermission = userHasRoleOnActiveChannel(
    [
      EPermissions.Owner,
      EPermissions.Admin,
      EPermissions.Analytics,
      EPermissions.Moderation,
      EPermissions.ModeratorRegistrant,
      EPermissions.PasscodeManager,
      EPermissions.Editor,
      EPermissions.GroupAdmin,
      EPermissions.Viewer,
      EPermissions.Builder,
      EPermissions.Producer,
    ],
    user
  );

  const shouldHaveGreenroomProducerUrl = useShowGreenroomButton(session);

  return (
    <OptionalComponent display={shouldHaveGreenroomProducerUrl && allowedPermission}>
      <TooltipPortal
        tooltipContent={
          publishedUrl ? (
            <></>
          ) : (
            <Text color="var(--gray-true-dark-100)" className="C2">
              Project not yet published{' '}
            </Text>
          )
        }
        disabled={disableTooltip || !!publishedUrl}
        keepOpenOnContentHover={false}
        {...tooltipProps}
      >
        <Button
          onClick={handleGoToGreenroom}
          disabled={loadingGreenroom || isProjectAutoGeneratedName}
          disabledOpacity={0.6}
          className={className || `L2 padding-v-8 padding-h-16 greenroom-primary-bg`}
          backgroundColor={backgroundColor || 'var(--primary-GR-500)'}
          hoverBackgroundColor={hoverBackgroundColor || 'var(--primary-GR-600)'}
          hoverOpacity={hoverOpacity ?? 1}
        >
          {loadingGreenroom ? <WaitingIndicator /> : TextComponent || 'Greenroom®'}
        </Button>
      </TooltipPortal>
    </OptionalComponent>
  );
};

export default GreenroomButton;
